import { useCallback, useEffect, useRef, useState } from 'react';
import type { ServerPart } from '../../navigation/searchGroups';
import { searchAPI, THING_KINDS, type ThingGroup, type ThingKind, type ThingRow } from '../../services/searchApi';

/** The server is asked from this many letters. Fewer find too much to be a short list. */
export const MIN_SEARCH_CHARS = 3;
/** The longest text the server takes. */
export const MAX_SEARCH_CHARS = 60;
/** How long after the last keystroke the server is asked. */
export const SEARCH_PAUSE_MS = 300;

/**
 * Where the server's part of a search stands, for the text now in the box:
 *   off      not asked: under three letters, or no project selected. Decided by the text
 *            and the project alone, never by an answer: the list cuts its page rows by
 *            it, and must cut them the same before and after one (navigation/searchGroups).
 *   waiting  still to be asked (the pause after a keystroke), or asked and not answered
 *   ready    answered
 *   failed   the whole call failed. Never "nothing found".
 */
export type ServerSearch = ServerPart;

const OFF: ServerSearch = { status: 'off' };
const WAITING: ServerSearch = { status: 'waiting' };
const FAILED: ServerSearch = { status: 'failed' };

const isKind = (value: unknown): value is ThingKind =>
  typeof value === 'string' && (THING_KINDS as readonly string[]).includes(value);

/** One result line, or null for a row that is not one. A thing with no name is named by its code. */
const readRow = (raw: unknown): ThingRow | null => {
  const row = raw as Partial<ThingRow> | null;
  if (!row || typeof row.id !== 'string' || !row.id) return null;
  const code = typeof row.code === 'string' && row.code.trim() ? row.code.trim() : undefined;
  const name = (typeof row.name === 'string' && row.name.trim()) || code;
  if (!name) return null;
  return { id: row.id, name, detail: typeof row.detail === 'string' ? row.detail : '', ...(code ? { code } : {}) };
};

/**
 * The groups of an answer, or null when it is not one. An answer of another shape is
 * never read as "nothing found". A kind this build does not know is left out.
 */
export function readGroups(body: unknown): ThingGroup[] | null {
  const answer = body as { success?: unknown; data?: { groups?: unknown } } | null;
  const groups = answer?.data?.groups;
  if (answer?.success !== true || !Array.isArray(groups)) return null;
  const out: ThingGroup[] = [];
  for (const raw of groups as Array<Partial<ThingGroup> | null>) {
    if (!raw || !isKind(raw.kind)) continue;
    if (raw.status === 'failed') {
      out.push({ kind: raw.kind, status: 'failed', rows: [], more: false });
    } else if (raw.status === 'ok' && Array.isArray(raw.rows)) {
      const rows = raw.rows.map(readRow).filter((row): row is ThingRow => row !== null);
      out.push({ kind: raw.kind, status: 'ok', rows, more: raw.more === true });
    } else {
      return null;
    }
  }
  return out;
}

/**
 * The server's part of the search pop-up: people, stores and the other named things
 * that hold the typed text, in the selected project.
 *
 * Asked once per pause, not per keystroke, and only from three letters with a project
 * selected. The answer is kept WITH the text and project it was asked for (as
 * pages/home/useRead keeps a number with its key): the moment either changes, the old
 * answer stops being shown, its request is aborted, and an answer that still arrives
 * for it is dropped. So what is on screen is always for the text in the box, and the
 * groups arrive together, once.
 *
 * An aborted request is not a failure and changes nothing. Closing the pop-up aborts
 * too. `retry` asks again at once for the same text.
 */
export function useSearchResults(text: string, projectId: number | null): { server: ServerSearch; retry: () => void } {
  const q = text.trim().replace(/\s+/g, ' ');
  const length = Array.from(q).length;
  const key = projectId !== null && length >= MIN_SEARCH_CHARS && length <= MAX_SEARCH_CHARS ? `${projectId}\n${q}` : null;

  const [held, setHeld] = useState<{ key: string; read: ServerSearch } | null>(null);
  const [again, setAgain] = useState(0);
  // Set by `retry`: the person asked, so there is no pause to wait out.
  const atOnce = useRef(false);

  useEffect(() => {
    if (key === null || projectId === null) return undefined;
    let current = true;
    const controller = new AbortController();
    const ask = () => {
      Promise.resolve()
        .then(() => searchAPI.find({ projectId, q }, controller.signal))
        .then(
          (res) => {
            if (!current) return;
            const groups = readGroups(res.data);
            setHeld({ key, read: groups ? { status: 'ready', groups } : FAILED });
          },
          () => {
            if (current && !controller.signal.aborted) setHeld({ key, read: FAILED });
          },
        );
    };
    const timer = setTimeout(ask, atOnce.current ? 0 : SEARCH_PAUSE_MS);
    atOnce.current = false;
    return () => {
      current = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [key, projectId, q, again]);

  const retry = useCallback(() => {
    atOnce.current = true;
    setHeld(null);
    setAgain((n) => n + 1);
  }, []);

  const server = key === null ? OFF : held && held.key === key ? held.read : WAITING;
  return { server, retry };
}
